import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { FormikProps } from 'formik';
import { toast } from 'react-toastify';
import {
    CommonEcwComponentProps,
    EcwFormValues,
    getOrderKey,
    isThinjOrderBlocked,
    RouteFieldOptions,
    RouteFieldOptionsThinj,
    SelectUnitOption,
    SiteFieldOptions,
} from '../EcwConstants';
import useRecordUsage from '../../../Hooks/useRecordUsage';
import { Inventory, RecordUsageRes } from '../../../redux/recordUsage/recordUsageApiSlice';
import { Form, InputGroup, OverlayTrigger, Popover, Table } from 'react-bootstrap';
import BarcodeInfo from '../InfoComponents/BarcodeInfo';
import { useDispatch } from 'react-redux';
import {
    ecwSelectedOrderInfo,
    setEcwSelectedBarcodeData,
} from '../../../redux/ecwOrder/ecwOrderSlice';
import ItemNameWarningDialog from '../InfoComponents/ItemNameWarningDialog';
import { useAppSelector } from '../../../redux/hooks';

interface EcwUsageDetailsFormProps extends CommonEcwComponentProps {
    formik: FormikProps<EcwFormValues>;
    isSelectedOrderProcessed: boolean;
    isThinjBlocked?: boolean;
}

const EcwUsageDetailsForm: React.FC<EcwUsageDetailsFormProps> = ({
    formik,
    setLoading,
    isSelectedOrderProcessed,
    isThinjBlocked,
}) => {
    const dispatch = useDispatch();
    const barcodeTimeoutRef = useRef<NodeJS.Timeout>();
    const barcodeInputRef = useRef<HTMLInputElement>(null);
    const prevOrderKeyRef = useRef<string | number | null>(null);
    const selectedOrderInfo = useAppSelector(ecwSelectedOrderInfo);

    const [barcodeData, setBarcodeData] = useState<RecordUsageRes | null>(null);
    const [useUnitsList, setUseUnitsList] = useState<SelectUnitOption[]>([]);
    const [isItemNameWarning, setIsItemNameWarning] = useState<boolean>(false);
    const [isImmunization, setIsImmunization] = useState<boolean>(false);

    const { getBarcodeDetailsAPI, getBarcodeDetailsLoading } = useRecordUsage();

    const { values, errors, touched, setFieldValue, handleChange, handleBlur, setFieldTouched, setFieldError, setSubmitting } = formik;

    const currentOrderKey = useMemo(() => {
        if (!values.orderId || !selectedOrderInfo) return null;
        return getOrderKey(selectedOrderInfo) ?? selectedOrderInfo.id ?? values.orderId ?? null;
    }, [values.orderId, selectedOrderInfo]);

    const latestSelectedOrderInfoRef = useRef(selectedOrderInfo);

    useEffect(() => {
        latestSelectedOrderInfoRef.current = selectedOrderInfo;
    }, [selectedOrderInfo]);

    // Reset dialogs on order change
    useEffect(() => {
        if (currentOrderKey !== prevOrderKeyRef.current) {
            prevOrderKeyRef.current = currentOrderKey;
            setIsItemNameWarning(false);
        }
    }, [currentOrderKey]);

    useEffect(() => {
        setLoading?.(getBarcodeDetailsLoading);
    }, [getBarcodeDetailsLoading, setLoading]);

    useEffect(() => {
        if (!values.barcode) {
            setBarcodeData(null);
            dispatch(setEcwSelectedBarcodeData(null));
            setUseUnitsList([]);
            setIsItemNameWarning(false);
        }
    }, [values.barcode, dispatch]);

    useEffect(() => {
        setFieldValue("itemInfo", {
            lotNumber: barcodeData?.lotNo ?? "",
            expiration: barcodeData?.expDate ?? "",
            ndcCode: barcodeData?.ndc ?? "",
            manufacturer: barcodeData?.manufacturer ?? "",
            cvXCode: barcodeData?.cvx ?? "",
            mvxCode: barcodeData?.mvxCode ?? "",
            mvxManufacturer: barcodeData?.mvxManufacturer ?? "",
            stock: barcodeData?.list2 ?? "",
            itemName: barcodeData?.rxName ?? "",
            itemType: barcodeData?.list1 ?? "",
        });
    }, [barcodeData, setFieldValue]);

    useEffect(() => {
        setIsImmunization(!!(selectedOrderInfo?.orderType === 'VAC' && selectedOrderInfo?.immId));
    }, [selectedOrderInfo]);

    const handleBarcodeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (isFormDisabled) return;
        if (!e.target.value) {
            setFieldTouched("barcode", true, true);
            return;
        }

        const missingFields = [];
        if (!values.patID) missingFields.push('patID');
        if (!values.orderId) missingFields.push('orderId');

        if (missingFields.length) {
            missingFields.forEach(field => setFieldTouched(field, true));
            return;
        }

        // Clear previous timeout
        if (barcodeTimeoutRef.current) {
            clearTimeout(barcodeTimeoutRef.current);
        }

        // Debounce barcode API call
        barcodeTimeoutRef.current = setTimeout(async () => {
            try {
                const params = isImmunization ? { isImmunization: true } : {};
                const res: RecordUsageRes = await getBarcodeDetailsAPI({
                    ...values,
                    query: "true",
                }, params);

                if (res.status === "F") {
                    setFieldError("barcode", res.msg);
                    setFieldTouched("barcode", true, false);
                    setSubmitting(true);
                    return;
                }

                setSubmitting(false);

                // Item name warning check (kept for medicationRequest e.g. MED orders)
                const latestOrder = latestSelectedOrderInfoRef.current;
                if (latestOrder?.medicationRequest) {
                    const orderItemName = latestOrder.medicationRequest?.medicationName?.trim()?.toLowerCase();
                    const barcodeItemName = res?.rxName?.trim()?.toLowerCase();

                    if (orderItemName && orderItemName !== barcodeItemName) {
                        setIsItemNameWarning(true);
                    }
                }

                /* In API response we get unitName as unitCode, using unitName for value and label */
                const updatedUseUnitsList: SelectUnitOption[] = res?.useUnits?.map(({ unitName, isDefault }) => ({
                    value: unitName,
                    label: unitName,
                    isDefault: !!isDefault,
                })) ?? [];

                const defaultUnit = updatedUseUnitsList.find(u => u.isDefault) || updatedUseUnitsList[0] || null;

                setBarcodeData(res);
                dispatch(setEcwSelectedBarcodeData(res));
                setUseUnitsList(updatedUseUnitsList);
                setFieldValue("units", defaultUnit?.value || "");

                toast.success(res.msg);

            } catch (error) {
                console.error("Barcode lookup failed:", error);
            }
        }, 500);
    };

    const handleItemNameWarningAction = useCallback((action: "accept" | "cancel") => {
        setIsItemNameWarning(false);
        if (action === "cancel") {
            setTimeout(() => {
                if (barcodeInputRef.current) {
                    barcodeInputRef.current.focus();
                    barcodeInputRef.current.select();
                }
            }, 0);
        }
    }, []);

    const routeOptions = useMemo(() => {
        return (selectedOrderInfo?.orderType === "THINJ" || values.orderType === "THINJ")
            ? RouteFieldOptionsThinj
            : RouteFieldOptions;
    }, [selectedOrderInfo?.orderType, values.orderType]);

    const isFormDisabled = isSelectedOrderProcessed || Boolean(isThinjBlocked ?? isThinjOrderBlocked(selectedOrderInfo));

    const quantityPopover = useMemo(() => (
        <Popover id="quantityPopover">
            <Popover.Body>
                <Table hover={true}>
                    <thead>
                        <tr>
                            <th>Quantity</th>
                            <th>Unit</th>
                            <th>Location</th>
                        </tr>
                    </thead>
                    <tbody>
                        {barcodeData?.inventory && barcodeData.inventory.length > 0 ? (
                            barcodeData.inventory.map((item: Inventory, index: number) => (
                                <tr key={index}>
                                    <td>{item?.netQty}</td>
                                    <td>{barcodeData?.uom}</td>
                                    <td>{item?.locationName}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={3} style={{ textAlign: 'center', color: 'red' }}>
                                    No inventory found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Popover.Body>
        </Popover>
    ), [barcodeData?.inventory, barcodeData?.uom]);

    return (
        <div className='item-usage-info-box'>
            <h4 className="form-section-title">Enter Item Usage Details</h4>
            <Form.Group className='form-field'>
                <Form.Label>Barcode*</Form.Label>
                <Form.Control
                    ref={barcodeInputRef}
                    type="text"
                    placeholder="Enter Barcode"
                    name="barcode"
                    value={values.barcode}
                    onChange={handleChange}
                    onBlur={handleBarcodeBlur}
                    isInvalid={touched.barcode && !!errors.barcode}
                    disabled={isFormDisabled}
                />
                {errors.barcode && touched.barcode && (
                    <Form.Control.Feedback type="invalid">{errors.barcode}</Form.Control.Feedback>
                )}
            </Form.Group>
            <div className='form-field-wrapper'>
                <Form.Group className='form-field'>
                    <Form.Label>Quantity*</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type="number"
                            placeholder="Enter Quantity"
                            name="qty"
                            value={values.qty}
                            onChange={handleChange}
                            onBlur={(e) => {
                                if (!isItemNameWarning) {
                                    handleBlur(e);
                                }
                            }}
                            isInvalid={touched.qty && !!errors.qty}
                            disabled={isFormDisabled}
                        />
                        {useUnitsList?.length > 0 && (
                            <Form.Select
                                aria-label="Select Quantity"
                                name="units"
                                className="unit-select"
                                value={values.units}
                                onChange={handleChange}
                                disabled={isFormDisabled}
                                title={useUnitsList?.find(item => item.value === values.units)?.label || ""}
                            >
                                {useUnitsList.map((unit: SelectUnitOption, index: number) => (
                                    <option key={index} value={unit.value}>
                                        {unit.label}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                    </InputGroup>
                    {errors.qty && touched.qty && (
                        <Form.Control.Feedback type="invalid">{errors.qty}</Form.Control.Feedback>
                    )}
                    {barcodeData?.inventory && (
                        <OverlayTrigger trigger="hover" placement="bottom" overlay={quantityPopover}>
                            <Form.Text id="quantity-hint" muted>Inventory</Form.Text>
                        </OverlayTrigger>
                    )}
                </Form.Group>
                <Form.Group className='form-field'>
                    <Form.Label>Waste</Form.Label>
                    <Form.Control
                        type="number"
                        placeholder="Enter Waste"
                        name="waste"
                        value={values.waste}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.waste && !!errors.waste}
                        disabled={isFormDisabled}
                    />
                    {errors.waste && touched.waste && (
                        <Form.Control.Feedback type="invalid">{errors.waste}</Form.Control.Feedback>
                    )}
                </Form.Group>
            </div>
            <Form.Group className='form-field'>
                <Form.Label>Route*</Form.Label>
                <Select
                    name='route'
                    options={routeOptions}
                    classNamePrefix="react-select"
                    placeholder="Please select route"
                    isDisabled={isFormDisabled}
                    onChange={(selectedItem) => setFieldValue("route", selectedItem?.value)}
                    onBlur={() => setFieldTouched("route", true, true)}
                    value={routeOptions.find(item => item.value === values.route) || null}
                    className={`${touched.route && errors.route ? "is-invalid-border" : ""} react-select-container`}
                />
                {errors.route && touched.route && (
                    <Form.Control.Feedback type="invalid">{errors.route}</Form.Control.Feedback>
                )}
            </Form.Group>
            <Form.Group className='form-field'>
                <Form.Label>Site*</Form.Label>
                <Select
                    name='site'
                    options={SiteFieldOptions}
                    classNamePrefix="react-select"
                    placeholder="Please select site"
                    isDisabled={isFormDisabled}
                    onChange={(selectedItem) => setFieldValue("site", selectedItem?.value)}
                    onBlur={() => setFieldTouched("site", true, true)}
                    value={SiteFieldOptions.find(item => item.value === values.site) || null}
                    className={`${touched.site && errors.site ? "is-invalid-border" : ""} react-select-container`}
                />
                {errors.site && touched.site && (
                    <Form.Control.Feedback type="invalid">{errors.site}</Form.Control.Feedback>
                )}
            </Form.Group>

            <BarcodeInfo barcodeInfo={barcodeData} />

            {isItemNameWarning && (
                <ItemNameWarningDialog
                    show={isItemNameWarning}
                    onAccept={() => handleItemNameWarningAction("accept")}
                    onCancel={() => handleItemNameWarningAction("cancel")}
                    orderItemName={selectedOrderInfo?.medicationRequest?.medicationName ?? ""}
                    barcodeItemName={barcodeData?.rxName ?? ""}
                />
            )}
        </div>
    );
};

export default EcwUsageDetailsForm;
